import React, {
  FC,
  KeyboardEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import usePlacesAutocomplete, { Suggestion } from 'use-places-autocomplete'
import Spinner from 'common/Spinner'
import _isEmpty from 'lodash/isEmpty'
import GoogleMapsContext from 'context/common/GoogleMapsContext'
import styles from './index.module.scss'
import AutoCompleteSuggestions from './AutoCompleteSuggestions'
import classNames from 'classnames'
import Icon from 'common/Icon'
import { NAVY, SLATE_GRAY } from 'constants/colors'
import { getFirstLocationResultData } from 'utils/common'
import { usedForFeature } from 'constants/logEvents'
import { PROVIDER_SEARCH_LOCATION_PLACE_TYPES } from 'constants/common'
import { cn } from '@/lib/utils'
import {
  Choose,
  If,
  Otherwise,
  When,
} from '@/utility_components/ControlStatements'

type LocationAutoCompleteProps = {
  showClearOption?: boolean
  showSearchIcon?: boolean
  onSelect: (result: GoogleLocationResult) => void
  prefillInputValue?: string
  // For customizations
  containerClassName?: string
  inputClassName?: string
  suggestionContainerClassName?: string
  suggestionItemClassName?: string
  disableAutoFocus?: boolean
  isSuggestionAbsolute?: boolean
}

const LocationAutoComplete: FC<LocationAutoCompleteProps> = ({
  onSelect,
  showClearOption = false,
  showSearchIcon = false,
  prefillInputValue = '',
  containerClassName = '',
  inputClassName = '',
  suggestionContainerClassName = '',
  suggestionItemClassName = '',
  disableAutoFocus = false,
  isSuggestionAbsolute = true,
}) => {
  // https://developers.google.com/maps/documentation/javascript/supported_types
  // ^ For more details on what do we want to show in the google autocomplete results
  const searchOptions = {
    types: PROVIDER_SEARCH_LOCATION_PLACE_TYPES,
  }
  const locationInputRef = useRef(null)
  const [inputValue, setInputValue] = useState(prefillInputValue)
  const { googleMapsScriptLoading } = useContext(GoogleMapsContext)
  const {
    init,
    suggestions,
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: 'us' },
      ...searchOptions,
    },
    debounce: 300,
    initOnMount: false,
    callbackName: 'initMap',
  })

  useEffect(() => {
    if (!disableAutoFocus && locationInputRef.current) {
      locationInputRef.current.focus()
    }
  }, [disableAutoFocus])

  useEffect(() => {
    if (prefillInputValue) {
      setInputValue(prefillInputValue)
    }
  }, [prefillInputValue])

  useMemo(() => {
    // scritly checking for only boolean false value
    if (googleMapsScriptLoading === false) {
      init()
    }
  }, [googleMapsScriptLoading])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setValue(value)
    setInputValue(value)
  }

  const handleSelect = async ({
    place_id,
    description,
    structured_formatting,
  }: Suggestion) => {
    try {
      const { firstResult, latLong } = await getFirstLocationResultData(
        place_id,
        usedForFeature.HeaderFilterLocationDropdown
      )

      setInputValue(description)
      onSelect({
        ...firstResult,
        ...latLong,
        ...structured_formatting,
      })
      clearSuggestions()
    } catch (error) {
      //TODO: error handling not required
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !_isEmpty(suggestions.data)) {
      handleSelect(suggestions.data[0])
    }
  }

  return (
    <>
      <div
        className={classNames('position-relative d-flex', containerClassName)}
      >
        <input
          ref={locationInputRef}
          autoComplete="off"
          disabled={googleMapsScriptLoading}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          value={inputValue}
          className={cn(
            '~outline-none ~border-none ~text-base ~w-full',
            inputClassName
          )}
          placeholder="City, State or Zip"
        />

        <If condition={suggestions.loading}>
          <div className="align-self-center ml-auto mr-1">
            <Spinner role="status" className="mr-2" size="sm" />
          </div>
        </If>

        <Choose>
          <When condition={showSearchIcon}>
            <Icon icon="magnify" size="24" fill={NAVY} style={{ zIndex: 1 }} />
          </When>
          <Otherwise>
            <If
              condition={
                !suggestions.loading && !!inputValue && showClearOption
              }
            >
              <button
                type="button"
                className={classNames(
                  styles.close_button,
                  'btn btn-none align-self-center ml-auto mr-1 flex-center p-0'
                )}
                onClick={() => setInputValue('')}
              >
                <Icon icon="close" className="fa-md" fill={SLATE_GRAY} />
              </button>
            </If>
          </Otherwise>
        </Choose>

        <If condition={isSuggestionAbsolute}>
          <AutoCompleteSuggestions
            onSelect={handleSelect}
            suggestions={suggestions}
            suggestionItemClassName={suggestionItemClassName}
            suggestionContainerClassName={suggestionContainerClassName}
            isSuggestionAbsolute
          />
        </If>
      </div>

      <If condition={!isSuggestionAbsolute}>
        <AutoCompleteSuggestions
          onSelect={handleSelect}
          suggestions={suggestions}
          suggestionItemClassName={suggestionItemClassName}
          suggestionContainerClassName={suggestionContainerClassName}
          isSuggestionAbsolute={false}
        />
      </If>
    </>
  )
}

export default LocationAutoComplete
